import React, { createContext, useContext, useEffect, useState } from 'react';
import supabase from '../supabase'; // Adjust the path to your supabase client

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // Development mode check
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    let mounted = true;

    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error.message);
        }
        
        if (mounted) {
          setSession(session);
          setUser(session?.user || null);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error:', error.message);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (isDevelopment) {
          console.log('Auth state changed:', event);
        }
        
        if (mounted) {
          setSession(session);
          setUser(session?.user || null);
          
          // Check if this is a password recovery session
          if (event === 'PASSWORD_RECOVERY') {
            setIsPasswordRecovery(true);
          } else if (event === 'SIGNED_IN' && !isPasswordRecovery) {
            // Only reset password recovery flag if it's a regular sign in
            setIsPasswordRecovery(false);
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, [isPasswordRecovery, isDevelopment]);

  // Sign up function
  const signUp = async (email, password, options = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          ...options,
          data: options.data || {}
        }
      });
      
      if (error) throw error;
      
      if (isDevelopment) {
        console.log('Signup successful');
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Signup error:', error.message);
      return { data: null, error };
    }
  };

  // Sign in function
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) throw error;
      
      if (isDevelopment) {
        console.log('Login successful');
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Login error:', error.message);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Sign in with OAuth
  const signInWithOAuth = async (provider, redirectTo = null) => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectTo || `${window.location.origin}/welcome`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          scopes: 'profile email'
        }
      });
      
      if (error) throw error;
      
      if (isDevelopment) {
        console.log('OAuth login initiated for provider:', provider);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('OAuth login error:', error.message);
      return { data: null, error };
    }
  };

  // Sign out function
  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Reset password recovery flag on sign out
      setIsPasswordRecovery(false);
      
      if (isDevelopment) {
        console.log('Logout successful');
      }
      
      return { error: null };
    } catch (error) {
      console.error('Logout error:', error.message);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Reset password function
  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });
      
      if (error) throw error;
      
      if (isDevelopment) {
        console.log('Password reset email sent');
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Reset password error:', error.message);
      return { data: null, error };
    }
  };

  // Update password function
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      // Reset password recovery flag after successful password update
      setIsPasswordRecovery(false);
      
      if (isDevelopment) {
        console.log('Password updated successfully');
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error.message);
      return { data: null, error };
    }
  };

  // Update user profile
  const updateProfile = async (updates) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: updates
      });
      
      if (error) throw error;
      
      if (isDevelopment) {
        console.log('Profile updated successfully');
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Update profile error:', error.message);
      return { data: null, error };
    }
  };

  const value = {
    user,
    session,
    loading,
    isPasswordRecovery,
    signUp,
    signIn,
    signInWithOAuth,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    // Modified authentication check to exclude password recovery sessions
    isAuthenticated: !!user && !!session && !isPasswordRecovery,
    // Helper to check if email confirmation is required and completed
    isEmailConfirmed: !!user?.email_confirmed_at
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};